import React from 'react';
import { useAuth } from '../context/AuthContext';

const ProfilePage = () => {
  const { user } = useAuth();

  // Mock data if user is not logged in or doesn't have these fields
  const profileData = {
    fullName: user?.full_name || 'Sarah Mitchell',
    role: 'Senior UX Strategist',
    location: 'San Francisco, CA',
    bio: '"I design digital experiences that bridge the gap between complex technology and human emotion, focused on sustainable systems and inclusive communities."',
    email: user?.email || 'hello@sarahmitchell.design',
    website: 'sarahmitchell.design',
    avatar: 'https://lh3.googleusercontent.com/aida-public/AB6AXuCRZkX_dzfpxCLD3PK6cOX_VDbPOGvZs-y0xppNJSe1XOqSG3CGDIM_rvsC1Hia2IhTnVSQROy39huf2siE9A5BAWOpQ6F-8-6m9s7vFiyd94jLzaEiBShubLRySq3ZFXJ2MFlWqo6s1BpNyyZJBPUA8tles9N4dXqJMXtoCcV0-jTVVMwxkfAf5MsIQBYcevPPAEkSXpv1FRxcThKosbkksTVdd0cJJvo82UvQRaNSibQ9tu7QDM9YXt-ZI-nwoVkQvL_5-yeT9Pg',
    stats: {
      curations: 14,
      followers: 82,
      following: 126
    },
    expertise: ['UX Strategy', 'Visual Design', 'Sustainability', 'Design Systems']
  };

  return (
    <div className="bg-background text-on-background min-h-screen pt-20">
      {/* Profile Header Section */}
      <header className="relative w-full h-[460px] flex items-center overflow-hidden">
        {/* Sophisticated Texture Background */}
        <div className="absolute inset-0 bg-emerald-950">
          <img 
            alt="Header texture" 
            className="w-full h-full object-cover opacity-30 mix-blend-overlay" 
            src="https://lh3.googleusercontent.com/aida-public/AB6AXuB6SNsaTCr9GDbyvjMvjkQrlfqsLZyZQWAfw8V2nZ0DPmoeUwhWvX5REeHLrp27Eh-yLmVqYnHtrxy-pS8xCS5B_iCco_WXnLFGUKYkpww42MkzRCHKHmGHVflGE2PFos4eXelcCAt0UnRiDiTm9EpKyEEkZ4vSmaxEKNq7uCMIaV6_GIWe-RrnpZQDcILkYTYu1Vt_aXV6WaNUQvf_i7JJW_JDxFZaCiQd1W-MgXRCACDYgQ_HJmCBncSapER-WQCMbwS7AiMw-OM"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-emerald-950 via-emerald-900/60 to-transparent"></div>
        </div>
        <div className="relative max-w-7xl mx-auto w-full px-8 md:px-12 pt-12 flex flex-col md:flex-row items-center md:items-end justify-between gap-10">
          <div className="flex flex-col md:flex-row items-center md:items-end gap-10">
            <div className="relative group">
              <div className="w-40 h-40 md:w-48 md:h-48 rounded-full border-[6px] border-white/10 shadow-2xl overflow-hidden bg-emerald-900 ring-1 ring-white/20">
                <img 
                  alt={profileData.fullName} 
                  className="w-full h-full object-cover" 
                  src={profileData.avatar}
                />
              </div>
              <div className="absolute bottom-4 right-4 bg-emerald-500 w-7 h-7 rounded-full border-4 border-emerald-950 shadow-lg"></div>
            </div>
            <div className="text-center md:text-left mb-4">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                <h1 className="text-5xl font-extrabold tracking-tight text-white">{profileData.fullName}</h1>
                <span className="material-symbols-outlined text-emerald-400 text-3xl">verified</span>
              </div>
              <p className="text-xl text-emerald-100/90 font-medium mb-4">{profileData.role}</p>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 text-emerald-50/70 text-sm font-medium">
                <span className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-sm">location_on</span> {profileData.location}
                </span>
                <span className="flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full border border-white/10">
                  <span className="material-symbols-outlined text-xs">work</span> Open to Consulting
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <button className="bg-white/5 hover:bg-white/10 backdrop-blur-md text-white w-12 h-12 rounded-full font-semibold transition-all flex items-center justify-center border border-white/20">
              <span className="material-symbols-outlined text-[20px]">share</span>
            </button>
            <button className="bg-emerald-50 hover:bg-white text-emerald-950 px-8 py-3.5 rounded-full font-bold shadow-xl transition-all flex items-center gap-3">
              <span className="material-symbols-outlined text-[20px]">edit</span>
              Edit Profile
            </button>
          </div>
        </div>
      </header>

      {/* Profile Tabs & Global Stats */}
      <div className="bg-white border-b border-emerald-50 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-8 md:px-12 flex flex-col md:flex-row justify-between items-center">
          <div className="flex gap-8 md:gap-12 overflow-x-auto no-scrollbar py-6">
            <button className="text-emerald-950 font-bold border-b-2 border-emerald-800 pb-1 flex items-center gap-2 whitespace-nowrap">Overview</button>
            <button className="text-slate-500 hover:text-emerald-800 transition-colors font-medium whitespace-nowrap">Saved Jobs</button>
            <button className="text-slate-500 hover:text-emerald-800 transition-colors font-medium whitespace-nowrap">Saved Housing</button>
            <button className="text-slate-500 hover:text-emerald-800 transition-colors font-medium whitespace-nowrap">My Curations</button>
          </div>
          {/* Stats Row */}
          <div className="hidden md:flex items-center gap-8 py-6 border-l border-emerald-50 pl-12">
            <div className="text-center">
              <p className="text-lg font-bold text-emerald-950 leading-none">{profileData.stats.curations}</p>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mt-1">Curations</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-emerald-950 leading-none">{profileData.stats.followers}</p>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mt-1">Followers</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-emerald-950 leading-none">{profileData.stats.following}</p>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mt-1">Following</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="max-w-7xl mx-auto px-8 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-16">
        {/* Left Column: Biography & Info */}
        <div className="lg:col-span-4 space-y-12">
          {/* About Card */}
          <section>
            <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-900/40 mb-6">Introduction</h2>
            <div className="bg-white rounded-2xl p-10 shadow-[0_8px_30px_rgb(0,0,0,0.02)] border border-emerald-50/50">
              <p className="text-slate-700 leading-[1.8] text-lg mb-8 font-light italic">
                {profileData.bio}
              </p>
              <div className="space-y-5 pt-8 border-t border-emerald-50/60">
                <div className="flex items-center gap-4 text-sm group cursor-pointer">
                  <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-700 group-hover:bg-emerald-700 group-hover:text-white transition-all">
                    <span className="material-symbols-outlined text-lg">language</span>
                  </div>
                  <span className="text-emerald-900 font-medium border-b border-emerald-100">{profileData.website}</span>
                </div>
                <div className="flex items-center gap-4 text-sm group cursor-pointer">
                  <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-700 group-hover:bg-emerald-700 group-hover:text-white transition-all">
                    <span className="material-symbols-outlined text-lg">mail</span>
                  </div>
                  <span className="text-slate-600">{profileData.email}</span>
                </div>
              </div>
            </div>
          </section>

          {/* Social Links */}
          <section className="flex justify-between p-2 bg-slate-50 rounded-2xl border border-slate-100">
            <button className="w-12 h-12 flex items-center justify-center rounded-xl bg-white text-slate-400 hover:text-emerald-700 hover:shadow-sm transition-all">
              <span className="material-symbols-outlined">link</span>
            </button>
            <button className="w-12 h-12 flex items-center justify-center rounded-xl bg-white text-slate-400 hover:text-emerald-700 hover:shadow-sm transition-all">
              <span className="material-symbols-outlined">public</span>
            </button>
            <button className="w-12 h-12 flex items-center justify-center rounded-xl bg-white text-slate-400 hover:text-emerald-700 hover:shadow-sm transition-all">
              <span className="material-symbols-outlined">alternate_email</span>
            </button>
            <button className="w-12 h-12 flex items-center justify-center rounded-xl bg-white text-slate-400 hover:text-emerald-700 hover:shadow-sm transition-all">
              <span className="material-symbols-outlined">groups</span>
            </button>
          </section>

          {/* Expertise Chips */}
          <section>
            <h3 className="text-xs font-bold text-emerald-900/40 uppercase tracking-[0.2em] mb-6">Core Expertise</h3>
            <div className="flex flex-wrap gap-3">
              {profileData.expertise.map((skill, index) => (
                <span 
                  key={index}
                  className="px-5 py-2.5 bg-white border border-emerald-100 text-emerald-900 font-semibold rounded-full text-xs hover:border-emerald-300 transition-colors shadow-sm"
                >
                  {skill}
                </span>
              ))}
            </div>
          </section>
        </div>

        {/* Right Column: Activity Feed / Editorial */}
        <div className="lg:col-span-8 space-y-12">
          <div className="flex items-center justify-between">
            <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-900/40">Portfolio & Insights</h2>
            <button className="text-emerald-800 font-bold text-xs flex items-center gap-2 group">
              VIEW ALL ACTIVITY <span className="material-symbols-outlined text-[18px] group-hover:translate-x-1 transition-transform">arrow_forward</span>
            </button>
          </div>

          {/* Editorial Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Featured Curation */}
            <div className="md:col-span-2 group">
              <div className="bg-white rounded-3xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.03)] border border-emerald-50 transition-all hover:shadow-[0_40px_80px_rgba(0,0,0,0.06)] hover:-translate-y-1">
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div className="relative h-72 md:h-auto overflow-hidden">
                    <img 
                      alt="High-rise design" 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" 
                      src="https://lh3.googleusercontent.com/aida-public/AB6AXuD17zpLFacvo6gMY2vHb1QoiJmONerQ1P0cP6Rg-xjA00N3HRDicbyp5YHxRz78eX4CwtbnyP9VV2ttCjo8BXaNawpj6STO69bkCdORnRzas8nmgkGZDtwGb30i97Nl9ghHTyXpsjEs59Ij1BB6rgnWxkD14CjYTpP8DxRzDhr78gj2e9yciBMzNS_u_n3PkPu-n_ODwJTyBOFuNbC7zM_X_dszSsKf7hhY9J6m7rTu7Z0tcKE1hSXag6wh6TsScx-hzEhzWhoIdVk"
                    />
                    <div className="absolute top-6 left-6 bg-emerald-950 text-white text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-widest backdrop-blur-md">Featured Collection</div>
                  </div>
                  <div className="p-12 flex flex-col justify-center">
                    <div className="flex items-center gap-2 text-slate-400 text-xs font-bold uppercase tracking-widest mb-6">
                      <span className="material-symbols-outlined text-sm">calendar_today</span> Oct 12, 2024
                    </div>
                    <h3 className="text-3xl font-extrabold text-emerald-950 leading-tight mb-6">Sustainability in High-Rise Design</h3>
                    <p className="text-slate-600 leading-relaxed mb-10">An curated editorial examining 14 architectural breakthroughs in net-zero vertical urbanism and high-density timber construction.</p>
                    <div className="flex items-center justify-between">
                      <div className="flex -space-x-4">
                        <div className="w-10 h-10 rounded-full border-2 border-white ring-1 ring-slate-100 overflow-hidden">
                          <img src="https://lh3.googleusercontent.com/aida-public/AB6AXuDmggi2WW7kfSI-aD-9fKSiPF0hfJe6gLkugCTbowuzwntyyJ9MLbX81OXLtWhW8ieCTcIJa4piVf9aGQ6Bb4gQ4RmsuDCuqnAOV1IylnE-UAoUKF4m1dTtNnAJ7tzAH2M5SAOKG-dlfaRXQXAiqmDGKbtaDdE1l3ihx3V0L96NKsEsSLM-QQQAcF7RHRmfgiquYj6bR1QpUgyTPiOFLazwFwa0QDpx_IWQqJl0-eYbzWO8IR2n0UD1fAPLIQjPC5TEU1e5cP2fhOc" alt="Contributor" />
                        </div>
                        <div className="w-10 h-10 rounded-full border-2 border-white ring-1 ring-slate-100 overflow-hidden">
                          <img src="https://lh3.googleusercontent.com/aida-public/AB6AXuBwc0OkMu8WEFn2QoztC0jjfz7-Adfcu-h0TJF65JCNB4Qlula8IZPQBR55qndWttztsb2-ViiiL19EgPC39OkM3pzrXUKz9yXHgFVKtRj_NOslQyh2wgbkjO09snZusOG6kd9mvRVdNjs2h_EmorY-M-BHGde2KJ1nxU97NnOIE2Uy_TfwNf9CytGyFb8boglXAIEA2usQa9Tx04OMs6vZcM_POQoZpfDN38EuxsBp9qG09pu8Kk6fkLxdqzqt2lATaLkF7acqAMQ" alt="Contributor" />
                        </div>
                        <div className="w-10 h-10 rounded-full border-2 border-white bg-emerald-50 flex items-center justify-center text-[10px] font-bold text-emerald-900">+12</div>
                      </div>
                      <button className="w-12 h-12 rounded-full bg-emerald-950 text-white flex items-center justify-center hover:bg-emerald-800 transition-colors">
                        <span className="material-symbols-outlined">arrow_outward</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Job Card */}
            <div className="bg-white p-10 rounded-3xl border border-emerald-50 shadow-[0_10px_40px_rgba(0,0,0,0.02)] flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-start mb-8">
                  <span className="bg-blue-50 text-blue-700 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest">SAVED OPPORTUNITY</span>
                  <span className="text-xs text-slate-300 font-medium">Yesterday</span>
                </div>
                <h3 className="text-xl font-bold text-emerald-950 mb-3">Director of Design</h3>
                <p className="text-emerald-700 font-semibold text-sm mb-6">@ EcoStream Systems</p>
                <div className="flex items-center gap-6 text-slate-500 text-xs font-medium">
                  <span className="flex items-center gap-1.5"><span className="material-symbols-outlined text-sm">schedule</span> Full-time</span>
                  <span className="flex items-center gap-1.5"><span className="material-symbols-outlined text-sm">payments</span> $180k - $240k</span>
                </div>
              </div>
              <button className="mt-10 w-full py-3.5 rounded-full border-2 border-emerald-900/10 text-emerald-900 text-xs font-bold hover:bg-emerald-900 hover:text-white transition-all">Apply for Role</button>
            </div>

            {/* Testimonial Card */}
            <div className="bg-emerald-900 p-10 rounded-3xl text-white shadow-xl relative overflow-hidden group">
              <div className="absolute -right-4 -top-4 opacity-10 group-hover:scale-110 transition-transform duration-700">
                <span className="material-symbols-outlined text-9xl">format_quote</span>
              </div>
              <div className="relative z-10 h-full flex flex-col justify-between">
                <p className="text-lg leading-relaxed italic mb-10 text-emerald-50">"Sarah's ability to navigate complex community dynamics and translate them into functional design is unparalleled in the industry."</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-emerald-700">
                    <img src="https://lh3.googleusercontent.com/aida-public/AB6AXuCOPfn5upReI__-pAzz8MTKEzBfZb0rEoBgaVSf3dXTvw0_6xhyf7Hktpkuy40c6JIGXhMXFHaT1rlHG3eR5s_geyOyHtGAXRnYMCs4jkKOIxGzjcW2mt5NtG9lAsRnRwIiHadwk6mG1Jc9TBINzoM3wAp311FRRTTWZFnL5-iEQTg-uuvbsN-Tlfmb-mRoqSd1mc8xycMDhPLBtUceVLX4FjkaCovo50gHd7ZiompTVpqU7tyJa4XN5UKOXR_Toe6DwFeryGseLMc" alt="Marcus Chen" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm">Marcus Chen</h4>
                    <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest">Strategic Research Expert</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Featured Housing */}
            <div className="md:col-span-2 relative h-96 rounded-3xl overflow-hidden group">
              <img 
                alt="The Canopy" 
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[2s]" 
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuAwvZ2IEfBSZG6RZcGVc3gCkf6tKafIfHwyxmdSAM4vWCG_FaYaHSU91OxZ-gS_rQSfDTfYBr2Jw43XyfzWNfVmlXE1VxmXafGLkojh-RC_FTqNGSxZxq8MSbeM3WVgvsBQllXZ0NCh1FLFW1u2ONfxappsRCGkIxJ8_qI9iucGVejS7CtdanhxLhjErvfnnfi0O-GIdTr6R6SU12VIX1OmWDwgXHFBxjvUQFKT0iS2X7VBNr2Lka0FHbBa7qvkyltTRA47LwqmIeQ"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-950 via-emerald-950/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-12 w-full flex items-end justify-between">
                <div>
                  <span className="bg-emerald-400 text-emerald-950 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest mb-4 inline-block">New Housing Save</span>
                  <h3 className="text-4xl font-extrabold text-white mb-3">The Canopy Residences</h3>
                  <p className="text-emerald-50/70 text-sm font-medium">3 Bed, 2 Bath • Sausalito, Waterfront District</p>
                </div>
                <div className="bg-white/10 backdrop-blur-2xl border border-white/20 p-6 rounded-2xl text-center min-w-[140px]">
                  <p className="text-white font-extrabold text-2xl leading-none">$6,400</p>
                  <p className="text-emerald-300 text-[10px] uppercase font-bold tracking-[0.2em] mt-2">Monthly Rent</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
